/**
 * Use this to distinguish a local dev environment pointing to dev/staging/production backend
 * from an actual deployment to dev.mach.exchange or staging.mach.exchange.
 *
 * This is useful if we want to locally test features that are not enabled on
 * dev.mach.exchange or staging.mach.exchange.
 *
 * By default we enable every feature locally, modify if needed.
 */

// Define all possible environment values as an enum
export enum Environment {
    LOCAL_DEV = 'LOCAL_DEV',
    LOCAL_STAGING = 'LOCAL_STAGING',
    LOCAL_PRODUCTION = 'LOCAL_PRODUCTION',
    LOCAL_TESTNET = 'LOCAL_TESTNET',
    LOCAL_TESTNET_DEV = 'LOCAL_TESTNET_DEV',
    DEV = 'DEV',
    STAGING = 'STAGING',
    PRODUCTION = 'PRODUCTION',
    TESTNET = 'TESTNET',
    TESTNET_DEV = 'TESTNET_DEV',
    FEATHER_PREVIEW = 'FEATHER_PREVIEW',
}

// Create an array of all environment values for validation purposes
export const ENVIRONMENT_KEYS = Object.values(Environment)

// Use type-safe environment array
const LOCAL_ANY: Environment[] = [
    Environment.LOCAL_DEV,
    Environment.LOCAL_STAGING,
    Environment.LOCAL_PRODUCTION,
    Environment.LOCAL_TESTNET,
    Environment.LOCAL_TESTNET_DEV,
]

export const FEATURE_GATE_KEYS = [
    'ENABLE_TESTNETS',
    'USE_TESTNETS_ONLY',
    'TRANSACTION_CAP',
    'ONRAMP',
    'OFFRAMP',
    'SHOW_TRON_CHAIN',
    'ENABLE_EXPERIMENTAL_CHAINS',
    'ENABLE_MANTLE_CHAIN',
    'ENABLE_CELO_CHAIN',
    'ENABLE_FEATHER',
    'V2_MIGRATION',
    'MILES_REQUIREMENT',
    'DISABLE_ABSTRACT_CONNECTOR',
] as const

export type FeatureGateKey = (typeof FEATURE_GATE_KEYS)[number]

export const FEATURE_GATES: Readonly<Record<FeatureGateKey, Environment[]>> = {
    ENABLE_TESTNETS: [Environment.LOCAL_TESTNET, Environment.TESTNET, Environment.TESTNET_DEV], // Whether we want to enable testnets as well
    USE_TESTNETS_ONLY: [Environment.LOCAL_TESTNET, Environment.TESTNET, Environment.TESTNET_DEV], // Whether we ONLY want to enable testnets.
    TRANSACTION_CAP: [Environment.LOCAL_TESTNET, Environment.TESTNET, Environment.TESTNET_DEV], // Whether we ONLY want to enable testnets.
    ONRAMP: LOCAL_ANY.concat([Environment.DEV]),
    OFFRAMP: LOCAL_ANY.concat([Environment.DEV]),
    SHOW_TRON_CHAIN: LOCAL_ANY.concat([Environment.DEV, Environment.STAGING]), // gate all tron token assets on the tron network until backend is working
    ENABLE_EXPERIMENTAL_CHAINS: LOCAL_ANY.concat([Environment.DEV, Environment.STAGING]), // Enable in non-prod environments
    ENABLE_MANTLE_CHAIN: LOCAL_ANY.concat([Environment.DEV, Environment.STAGING]), // Enable in non-prod environments
    ENABLE_CELO_CHAIN: LOCAL_ANY.concat([Environment.DEV, Environment.STAGING]),
    ENABLE_FEATHER: LOCAL_ANY.concat([Environment.FEATHER_PREVIEW]), // Use Feather Orders
    V2_MIGRATION: LOCAL_ANY.concat([Environment.DEV, Environment.STAGING]), // Enable v2 migration feature in non-prod environments
    MILES_REQUIREMENT: [Environment.LOCAL_TESTNET, Environment.TESTNET, Environment.TESTNET_DEV], // Gate MON/WMON purchases behind miles requirement on testnet
    DISABLE_ABSTRACT_CONNECTOR: [Environment.LOCAL_TESTNET, Environment.TESTNET, Environment.TESTNET_DEV], // Disable Abstract Connector on testnet environments
}
